import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const EASE = [0.2, 0.8, 0.2, 1];

export const Reveal = ({ children, delay = 0, y = 40, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-80px" }}
    transition={{ duration: 0.9, ease: EASE, delay }}
  >
    {children}
  </motion.div>
);

// Line-by-line masked reveal for large display type
export const MaskedLines = ({ lines, className = "", lineClass = "", stagger = 0.12, delay = 0.2 }) => (
  <div className={className}>
    {lines.map((line, i) => (
      <span key={i} className="block overflow-hidden pb-[0.2em]">
        <motion.span
          className={`block ${lineClass}`}
          initial={{ y: "110%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1, ease: EASE, delay: delay + i * stagger }}
        >
          {line}
        </motion.span>
      </span>
    ))}
  </div>
);

export const FramedImage = ({ src, alt, className = "", imgClass = "" }) => {
  // Native loading="lazy" + framer whileInView are unreliable with smooth-scroll (Lenis):
  // drive both loading and the reveal from explicit IntersectionObservers instead.
  const frameRef = useRef(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = frameRef.current;
    if (!el) return;
    const loadIO = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldLoad(true);
          loadIO.disconnect();
        }
      },
      { rootMargin: "800px 0px" }
    );
    const revealIO = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setInView(true);
          revealIO.disconnect();
        }
      },
      { rootMargin: "-60px" }
    );
    loadIO.observe(el);
    revealIO.observe(el);
    return () => {
      loadIO.disconnect();
      revealIO.disconnect();
    };
  }, []);

  return (
    <div ref={frameRef} className={`relative overflow-hidden bg-[#eaeae6] ${className}`}>
      <motion.div
        className="h-full w-full"
        initial={{ clipPath: "inset(100% 0% 0% 0%)" }}
        animate={inView ? { clipPath: "inset(0% 0% 0% 0%)" } : {}}
        transition={{ duration: 1.1, ease: EASE }}
      >
        <motion.img
          src={shouldLoad ? src : undefined}
          alt={alt}
          decoding="async"
          className={`h-full w-full object-cover ${imgClass}`}
          initial={{ scale: 1.25 }}
          animate={inView ? { scale: 1 } : {}}
          transition={{ duration: 1.4, ease: EASE }}
        />
      </motion.div>
    </div>
  );
};
